import { useCallback, useEffect, useState } from 'react';
import { Anchor, Card, Divider, Grid, Text, Title } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  DEFAULT_NEWSLETTER_CONSENT_TEXT,
  DEFAULT_NEWSLETTER_OPT_IN,
  type BrandSettings,
  type NewsletterOptIn,
  type ThemeOverrides,
} from '@quizee/core';
import { Link } from 'react-router-dom';
import { api, errorMessage, type OrgSettingsPatch } from '../api/client';
import type { OrgSettingsView } from '../api/types';
import { useAuth } from '../auth/AuthContext';
import {
  consentChoiceOf,
  consentPatchValue,
  inheritedOptIn,
  isEmptyTheme,
  themeProblems,
  tidiedDifference,
  type ConsentChoice,
} from '../brand/inheritance';
import { resolvePreview } from '../brand/preview';
import { ConsentRead, ConsentSection, SavedDifference } from '../components/ConsentSection';
import { ErrorNote } from '../components/ErrorNote';
import { Loading } from '../components/Loading';
import { NewsletterRead, NewsletterSection } from '../components/NewsletterSection';
import { ReadOnlyNote } from '../components/OverrideField';
import { ReadEdit } from '../components/ReadEdit';
import { ThemeRead, ThemeSection } from '../components/ThemeSection';
import { PreviewCaption, WidgetPreview } from '../components/WidgetPreview';

/**
 * The publisher's own theme, which every brand starts from.
 *
 * There is nothing under this level except the quizee default, so every field here is either set
 * by the publisher or left to us, and it says which.
 */
export function OrgTheme() {
  return <OrgDefaults section="theme" />;
}

/**
 * The publisher's own consent notice, on the same terms: a default a brand can answer for itself.
 *
 * It carries the default opt in mode too, under the notice, because the two are one subject.
 */
export function OrgConsent() {
  return <OrgDefaults section="consent" />;
}

/** What the consent form holds while it is open: the choice and the words that go with it. */
interface ConsentDraft {
  readonly choice: ConsentChoice;
  readonly text: string;
}

/**
 * One of the two publisher defaults, on its own page.
 *
 * Theme and consent get a page each because they are looked at for different reasons: one is an
 * argument about colours, the other is a sentence a lawyer reads. Opt in mode is on the consent
 * page rather than a third one, because it is not a separate subject: the notice says what a
 * reader is agreeing to and the mode says what then happens to the address they gave, and a
 * publisher deciding either is deciding about consent.
 *
 * Each of the three is its own read and edit section, because they are looked at and changed one
 * at a time. They share the record they are stored in, but not a save: the API merges the fields a
 * body names and leaves the rest alone, so each section sends its own and never mentions the other
 * two. That matters with more than one person in the settings: saving a theme cannot put back a
 * consent notice as it was when this page loaded, over the wording a colleague changed since.
 */
function OrgDefaults({ section }: { section: 'theme' | 'consent' }) {
  const { org } = useAuth();
  const orgId = org?.org_id ?? null;
  const orgName = org?.name ?? 'this publisher';
  const isOwner = org?.role === 'owner';

  const [view, setView] = useState<OrgSettingsView | null>(null);
  const [loadProblem, setLoadProblem] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [savedDifference, setSavedDifference] = useState<string | null>(null);
  const [savedNewsletterDifference, setSavedNewsletterDifference] = useState<string | null>(null);

  // What the preview draws while somebody is editing. Null means it draws what is saved.
  const [themeDraft, setThemeDraft] = useState<ThemeOverrides | null>(null);
  const [consentDraft, setConsentDraft] = useState<ConsentDraft | null>(null);

  const load = useCallback(() => {
    if (orgId === null) return;
    setLoadProblem(null);
    setView(null);
    api
      .orgSettings(orgId)
      .then(setView)
      .catch((error: unknown) => setLoadProblem(errorMessage(error)));
  }, [orgId]);

  useEffect(load, [load]);

  /**
   * Send the fields the section being saved owns, and nothing else.
   *
   * A field the body does not name is left as it is, so this says nothing about the parts of the
   * record nobody was editing rather than sending them back as they were loaded. Null still means
   * "nothing set here", which is how a section says a value has been cleared.
   */
  async function saveWith(changed: OrgSettingsPatch): Promise<boolean> {
    if (orgId === null) return false;
    setProblem(null);
    setSavedDifference(null);
    setSavedNewsletterDifference(null);

    try {
      const next = await api.saveOrgSettings(orgId, changed);
      setView(next);
      setSavedDifference(tidiedDifference(changed.consent_text, next.settings.consent_text));
      setSavedNewsletterDifference(
        tidiedDifference(changed.newsletter_consent_text, next.settings.newsletter_consent_text),
      );
      notifications.show({ message: 'Defaults saved.', color: 'violet' });
      return true;
    } catch (error) {
      setProblem(errorMessage(error));
      return false;
    }
  }

  // The preview follows whichever section is open, and what is saved when none of them is.
  const themeShown = themeDraft ?? view?.settings.theme ?? {};
  const consentShown =
    consentDraft === null
      ? (view?.settings.consent_text ?? null)
      : consentPatchValue(consentDraft.choice, consentDraft.text);
  const draft: BrandSettings = {
    ...(isEmptyTheme(themeShown) ? {} : { theme: themeShown }),
    ...(consentShown === null ? {} : { consent_text: consentShown }),
  };
  const resolved = view === null ? null : resolvePreview(draft, undefined, view.resolved);
  /**
   * What the newsletter wording falls back to when this publisher has not written one.
   *
   * There is no layer between the publisher and us, so this is both the inherited value and the
   * bottom of the walk. An older reply carries no system default, and reading that absence as the
   * quizee default is right rather than a guess: an API that does not answer with the field is one
   * that never let anybody set it.
   */
  const newsletterSystem =
    view?.system_defaults.newsletter_consent_text ?? DEFAULT_NEWSLETTER_CONSENT_TEXT;

  return (
    <>
      <Title order={1} size="h2">
        {section === 'theme' ? 'Theme' : 'Consent notice'}
      </Title>
      <Text size="sm" c="dimmed" mt="sm" mb="lg">
        {section === 'theme'
          ? `The look every brand under ${orgName} starts from.`
          : `The line every brand under ${orgName} shows readers before an answer is recorded, and what happens to an email address a reader gives you afterwards.`}{' '}
        Brands inherit these settings unless they set their own.{' '}
        <Anchor component={Link} to="/settings/brands">
          See your brands
        </Anchor>
        .
      </Text>

      {loadProblem !== null && <ErrorNote message={loadProblem} onRetry={load} />}
      {loadProblem === null && view === null && <Loading label="Loading your defaults" />}

      {view !== null && resolved !== null && (
        <Grid gap="xl">
          <Grid.Col span={{ base: 12, md: 7 }}>
            {!isOwner && <ReadOnlyNote what="defaults" owner={orgName} />}
            {problem !== null && <ErrorNote message={problem} />}

            {section === 'theme' ? (
              <ReadEdit<ThemeOverrides>
                noun="the theme"
                canEdit={isOwner}
                open={() => view.settings.theme ?? {}}
                check={(theme) => themeProblems(theme)}
                onDraft={setThemeDraft}
                save={(theme) => saveWith({ theme: isEmptyTheme(theme) ? null : theme })}
                form={(theme, set) => (
                  <ThemeSection
                    theme={theme}
                    onChange={set}
                    below={[]}
                    system={view.system_defaults.theme}
                    canEdit
                    idPrefix="org"
                  />
                )}
              >
                <ThemeRead
                  theme={view.settings.theme}
                  below={[]}
                  system={view.system_defaults.theme}
                />
              </ReadEdit>
            ) : (
              <>
                <Text size="sm" c="dimmed" mb="md">
                  Publishing freezes this into the version readers see, so changing it here leaves
                  anything already live showing the older wording until it is published again. Each
                  brand lists what that applies to.
                </Text>

                <ReadEdit<ConsentDraft>
                  noun="the consent notice"
                  canEdit={isOwner}
                  open={() => ({
                    choice: consentChoiceOf(view.settings.consent_text),
                    // The box keeps something to work from when the choice is inherit or none, so
                    // switching to writing one does not start from a blank line.
                    text:
                      view.settings.consent_text !== null && view.settings.consent_text !== ''
                        ? view.settings.consent_text
                        : view.system_defaults.consent_text,
                  })}
                  onDraft={setConsentDraft}
                  save={(consent) =>
                    saveWith({
                      consent_text: consentPatchValue(consent.choice, consent.text.trim()),
                    })
                  }
                  form={(consent, set) => (
                    <ConsentSection
                      choice={consent.choice}
                      text={consent.text}
                      onChoice={(choice) => set({ ...consent, choice })}
                      onText={(text) => set({ ...consent, text })}
                      below={[]}
                      system={view.system_defaults.consent_text}
                      canEdit
                      scope="org"
                      idPrefix="org"
                    />
                  )}
                >
                  <ConsentRead
                    value={view.settings.consent_text}
                    below={[]}
                    system={view.system_defaults.consent_text}
                    scope="org"
                  />
                  <SavedDifference text={savedDifference} />
                </ReadEdit>

                <Divider my="xl" />

                <Title order={2} size="h3" mb="xs">
                  Newsletter signups
                </Title>
                <Text size="sm" mb="md">
                  The same decision one step further on. The notice above is what a reader agrees to
                  when they answer. These two are about the email address they hand over afterwards:
                  what they are agreeing to receive, and what then happens to the address. Both
                  apply to every brand that has not answered for itself.
                </Text>

                <Title order={3} size="h5" mb="xs">
                  Newsletter wording
                </Title>
                <ReadEdit<ConsentDraft>
                  noun="the newsletter wording"
                  canEdit={isOwner}
                  open={() => ({
                    choice: consentChoiceOf(view.settings.newsletter_consent_text ?? null),
                    text:
                      view.settings.newsletter_consent_text !== null &&
                      view.settings.newsletter_consent_text !== undefined &&
                      view.settings.newsletter_consent_text !== ''
                        ? view.settings.newsletter_consent_text
                        : newsletterSystem,
                  })}
                  save={(consent) =>
                    saveWith({
                      newsletter_consent_text: consentPatchValue(
                        consent.choice,
                        consent.text.trim(),
                      ),
                    })
                  }
                  form={(consent, set) => (
                    <ConsentSection
                      choice={consent.choice}
                      text={consent.text}
                      onChoice={(choice) => set({ ...consent, choice })}
                      onText={(text) => set({ ...consent, text })}
                      below={[]}
                      system={newsletterSystem}
                      canEdit
                      scope="org"
                      subject="newsletter"
                      idPrefix="org-newsletter"
                    />
                  )}
                >
                  <ConsentRead
                    value={view.settings.newsletter_consent_text ?? null}
                    below={[]}
                    system={newsletterSystem}
                    scope="org"
                    subject="newsletter"
                  />
                  <SavedDifference text={savedNewsletterDifference} />
                </ReadEdit>

                <Title order={3} size="h5" mt="lg" mb="xs">
                  How a signup is taken
                </Title>
                <ReadEdit<NewsletterOptIn | null>
                  noun="the opt in mode"
                  canEdit={isOwner}
                  open={() => view.settings.newsletter_opt_in ?? null}
                  save={(optIn) => saveWith({ newsletter_opt_in: optIn })}
                  form={(optIn, set) => (
                    <NewsletterSection
                      value={optIn}
                      inherited={inheritedOptIn(
                        [],
                        view.system_defaults.newsletter_opt_in ?? DEFAULT_NEWSLETTER_OPT_IN,
                      )}
                      onChange={set}
                      canEdit
                      scope="org"
                      idPrefix="org"
                    />
                  )}
                >
                  <NewsletterRead
                    value={view.settings.newsletter_opt_in ?? null}
                    inherited={inheritedOptIn(
                      [],
                      view.system_defaults.newsletter_opt_in ?? DEFAULT_NEWSLETTER_OPT_IN,
                    )}
                    scope="org"
                  />
                </ReadEdit>
              </>
            )}
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 5 }}>
            <Card withBorder radius="lg" p="md" style={{ position: 'sticky', top: 88 }}>
              <Title order={2} size="h4" mb="xs">
                What a reader sees
              </Title>
              <WidgetPreview theme={resolved.theme} segments={resolved.consent_segments} />
              <PreviewCaption />
            </Card>
          </Grid.Col>
        </Grid>
      )}
    </>
  );
}
